import { Theme } from '@/utils/constants';
import { useMediaQuery } from '@material-ui/core';
import { useTheme } from '@material-ui/core/styles';
import { size } from 'lodash/fp';
import { useCallback, useMemo, useState } from 'react';

interface UseShadowOffsetProps {
  shadowOffset: boolean;
  shadowTheme: {
    desktop: Theme;
    mobile: Theme;
  };
}

interface SwiperShadowDirections {
  beginning: boolean;
  end: boolean;
}

export const useShadowOffset = ({
  shadowOffset = false,
  shadowTheme = {
    mobile: Theme.light,
    desktop: Theme.dark,
  },
}: UseShadowOffsetProps) => {
  const [shadowDirection, setShadowDirection] = useState<SwiperShadowDirections>({
    beginning: false,
    end: false,
  });
  const globalTheme = useTheme();
  const isMobile = useMediaQuery(globalTheme.breakpoints.down('sm'));

  const localShadowTheme = useMemo(
    () => (isMobile ? shadowTheme.mobile : shadowTheme.desktop),
    [isMobile, shadowTheme],
  );

  const handleRealIndexChange = useCallback(
    (swiperInstance) => {
      const { snapIndex, snapGrid } = swiperInstance;
      const navigationLength = size(snapGrid);

      if (navigationLength <= 1) {
        setShadowDirection({ beginning: false, end: false });
        return;
      }

      if (snapIndex > 0 && snapIndex < navigationLength - 1) {
        setShadowDirection({ beginning: true, end: true });
        return;
      }

      setShadowDirection({ beginning: snapIndex === 0, end: snapIndex === navigationLength - 1 });
    },
    [setShadowDirection],
  );

  const shadowStyleClasses = useMemo(() => {
    if (!shadowOffset) return '';
    return `swiper-shadow swiper-shadow--theme-${localShadowTheme} ${
      shadowDirection.beginning ? 'swiper-shadow--right' : ''
    } ${shadowDirection.end ? 'swiper-shadow--left' : ''}`;
  }, [shadowOffset, shadowDirection, localShadowTheme]);

  return {
    handleRealIndexChange,
    shadowStyleClasses,
  };
};
